import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, QrCode } from 'lucide-react'
import { Button } from '@ascleo/ui/components/ui/button'
import { useCheckInSend } from '../context'
import { useAudio } from '../../../../../lib/use-audio'
import { useKimAddress } from '../../../../../lib/use-kim-address'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { canRenderKimAddressQrCode, KimAddressQrCode } from './KimAddressQrCode'
import {
  InsurerResultList,
  InsurerSearchField,
  useInsurerSearch,
} from './insurer-search'

const INSURER_SEARCH_INPUT_ID = 'insurer-search-selection-input'

/**
 * Insurer Search Selection Step.
 *
 * Searchable list of statutory (GKV) insurers. The insurance-app QR code is a
 * collapsed secondary option, so the search stays the main task (KC-49).
 *
 * @since 0.1.0
 * @example
 * ```tsx
 * <InsurerSearchSelectionStep />
 * ```
 */
export function InsurerSearchSelectionStep() {
  const { t } = useTranslation()
  const send = useCheckInSend()
  const search = useInsurerSearch()
  const { kimInfo } = useKimAddress()
  const [showApp, setShowApp] = useState(false)

  // Play audio on mount
  useAudio('checkIn-insurerSearchSelection')

  useEffect(function focusInsurerSearch() {
    document.getElementById(INSURER_SEARCH_INPUT_ID)?.focus()
  }, [])

  return (
    <KioskStep
      width="narrow"
      title={t('self-service.check-in.insurer-search.title')}
      lead={t('self-service.check-in.insurer-search.subtitle')}
    >
      <InsurerSearchField
        id={INSURER_SEARCH_INPUT_ID}
        testId="insurer-search-selection-input"
        search={search}
      />

      {search.failed ? (
        <KioskStatePanel
          tone="error"
          title={t('self-service.check-in.insurer-search.search-error-v2')}
          testId="frontdesk-insurer-search-selection-error"
        />
      ) : null}

      {search.results.length > 0 ? (
        <InsurerResultList
          results={search.results}
          testIdPrefix="insurer-search-result"
          onSelect={(insurer) =>
            send({ type: 'INSURER_SELECTED', insurerId: insurer.iknr, insurerName: insurer.name })
          }
        />
      ) : null}

      {search.hasSearched && search.results.length === 0 ? (
        <KioskStatePanel
          tone="neutral"
          title={t('self-service.check-in.insurer-search.no-results')}
          body={t('self-service.check-in.insurer-search.no-results-hint')}
        />
      ) : null}

      {canRenderKimAddressQrCode(kimInfo?.kimAddress) ? (
        <div className="flex min-w-0 flex-col gap-4">
          <Button
            type="button"
            variant="outline"
            size="2xl"
            className="self-start"
            aria-expanded={showApp}
            data-testid="insurer-search-app-alternative-btn"
            onClick={() => setShowApp((current) => !current)}
          >
            <QrCode aria-hidden className="size-6" />
            {t('self-service.check-in.insurer-search.app-alternative')}
            <ChevronDown
              aria-hidden
              className={`size-6 transition-transform ${showApp ? 'rotate-180' : ''}`}
            />
          </Button>
          {showApp && kimInfo ? (
            <KimAddressQrCode
              kimAddress={kimInfo.kimAddress}
              practiceName={kimInfo.practiceName}
              size={180}
            />
          ) : null}
        </div>
      ) : null}
    </KioskStep>
  )
}
